<template>
  <PreviewBase
    v-if="type === 'employee'"
    :loading="previewLoading"
  >
    <template #loading>
      <EmployeeAcknowledgementPreviewSkeleton />
    </template>

    <div
      v-if="previewData"
      class="employee-acknowledgement-preview"
    >
      <section class="preview-section">
        <div class="preview-name-row">
          <div class="text-h1">
            {{ previewData.first_name }} {{ previewData.last_name }}
          </div>
          <StatusPill
            v-if="!isEmployeeActive"
            status-key="inactive"
            label="Inactive"
          />
        </div>
        <div
          v-if="previewData.email"
          class="text-body-1 text-textLight preview-email"
        >
          {{ previewData.email }}
        </div>
      </section>

      <section class="preview-section">
        <div class="text-h2 section-title">
          {{ !isEmployeeActive ? 'Most Recent Position' : 'Current Position' }}
        </div>
        <div class="preview-info-grid text-body-2 text-textLight">
          <span class="preview-info-label text-textDark font-weight-bold">Current Job Profile</span>
          <span>{{ previewData.job_profile_title || '—' }}</span>
          <span class="preview-info-label text-textDark font-weight-bold">Job Code</span>
          <span>{{ previewData.hris_job_code || '—' }}</span>
          <span class="preview-info-label text-textDark font-weight-bold">Department</span>
          <span>{{ previewData.department_name || '—' }}</span>
        </div>
      </section>

      <EmployeeAcknowledgementCurrent
        v-if="currentAcknowledgement && isEmployeeActive"
        :acknowledgement="currentAcknowledgement"
      >
        <template
          v-if="showInlineCreateButton"
          #footer
        >
          <HideRbac :permissions="['createEmployeeJobProfileAcknowledgements']">
            <DpplDarkTooltip
              v-if="!createBannerActionEnabled"
              tooltip-placement="bottom"
              light-mode
            >
              <template #activator="{ onMouseenter, onMouseleave }">
                <span
                  class="inline-create-disabled-wrapper mt-2"
                  @mouseenter="onMouseenter"
                  @mouseleave="onMouseleave"
                >
                  <v-btn
                    color="brandPrimary"
                    variant="flat"
                    size="small"
                    disabled
                  >
                    Create Acknowledgement
                  </v-btn>
                </span>
              </template>
              <template #tooltip-content>
                <p class="text-body-2 text-textDark">
                  {{ createBannerDisabledTooltip }}
                </p>
              </template>
            </DpplDarkTooltip>
            <v-btn
              v-else
              color="brandPrimary"
              variant="flat"
              size="small"
              class="mt-2"
              @click="openCreateAcknowledgementModal"
            >
              Create Acknowledgement
            </v-btn>
          </HideRbac>
        </template>
      </EmployeeAcknowledgementCurrent>
      <section
        v-if="isEmployeeActive && showingAnyBanner"
        class="d-flex flex-column ga-4"
      >
        <div
          v-if="isEmployeeActive && currentAcknowledgement?.status === 'pending'"
          class="preview-section preview-section-banner"
        >
          <EmployeeAcknowledgementPreviewIncompleteBanner
            :days-pending="daysPending"
            :can-send-reminder="canSendReminder"
            :send-reminder-disabled-tooltip="sendReminderDisabledTooltip"
            @send-reminder="openReminderModal"
            @cancel="openCancelAcknowledgementModal"
          />
        </div>
        <div
          v-if="isEmployeeActive && showNoCompletedAcknowledgementBanner"
          class="preview-section preview-section-banner"
        >
          <EmployeeAcknowledgementPreviewNoAcknowledgementBanner
            :is-action-enabled="createBannerActionEnabled"
            :disabled-tooltip="createBannerDisabledTooltip"
            @create="openCreateAcknowledgementModal"
          />
        </div>
        <div
          v-if="isEmployeeActive && showAcknowledgementOutOfDateBanner"
          class="preview-section preview-section-banner"
        >
          <EmployeeAcknowledgementPreviewOutOfDateBanner
            :acknowledgement-cycle-sla="acknowledgementCycleSla"
            :is-action-enabled="createBannerActionEnabled"
            :disabled-tooltip="createBannerDisabledTooltip"
            @create="openCreateAcknowledgementModal"
          />
        </div>
        <div
          v-if="isEmployeeActive && showJobProfileChangedBanner"
          class="preview-section preview-section-banner"
        >
          <EmployeeAcknowledgementPreviewJobProfileChangedBanner
            :description="jobProfileChangedBannerDescription"
            :is-action-enabled="createBannerActionEnabled"
            :is-compare-enabled="compareJobProfilesEnabled"
            :disabled-tooltip="createBannerDisabledTooltip"
            :compare-disabled-tooltip="compareDisabledTooltip"
            @create="openCreateAcknowledgementModal"
            @compare-job-profiles="openCompareJobProfiles"
          />
        </div>
      </section>

      <EmployeeAcknowledgementHistory
        :acknowledgements="previewData.acknowledgements || []"
        @cancel="handleHistoryCancel"
      />
    </div>
  </PreviewBase>
</template>

<script setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import PreviewBase from '@/components/shared/PreviewBase.vue';
import EmployeeAcknowledgementPreviewSkeleton
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementPreviewSkeleton.vue';
import EmployeeAcknowledgementCurrent
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementCurrent.vue';
import EmployeeAcknowledgementHistory
  from '@/components/library/employeeAcknowledgements/EmployeeAcknowledgementHistory.vue';
import EmployeeAcknowledgementPreviewIncompleteBanner
  from '@/components/library/employeeAcknowledgements/banners/EmployeeAcknowledgementPreviewIncompleteBanner.vue';
import EmployeeAcknowledgementPreviewJobProfileChangedBanner from
  '@/components/library/employeeAcknowledgements/banners/EmployeeAcknowledgementPreviewJobProfileChangedBanner.vue';
import EmployeeAcknowledgementPreviewNoAcknowledgementBanner from
  '@/components/library/employeeAcknowledgements/banners/EmployeeAcknowledgementPreviewNoAcknowledgementBanner.vue';
import EmployeeAcknowledgementPreviewOutOfDateBanner
  from '@/components/library/employeeAcknowledgements/banners/EmployeeAcknowledgementPreviewOutOfDateBanner.vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import StatusPill from '@/components/shared/StatusPill.vue';
import { useEmployeeAcknowledgementsStore } from '@/stores/jobProfiles/employeeAcknowledgements';
import { useAcknowledgementsConfigStore } from '@/stores/jobProfiles/acknowledgementsConfig';

const route = useRoute();

const { previewData, previewLoading } = storeToRefs(useEmployeeAcknowledgementsStore());
const { acknowledgementCycleSla } = storeToRefs(useAcknowledgementsConfigStore());
const {
  openCreateModal,
  openSendReminderModal,
  openCancelModal,
  loadPreview,
  clearPreview,
  openCompareModal,
} = useEmployeeAcknowledgementsStore();
const { loadConfig: loadAcknowledgementsConfig } = useAcknowledgementsConfigStore();

const type = computed(() => route.query?.type);
const employeeUlid = computed(() => route.query?.preview);

const currentAcknowledgement = computed(() => {
  if (!previewData.value?.acknowledgements) return null;

  return previewData.value.acknowledgements.find((ack) => ack.is_current);
});

const showNoCompletedAcknowledgementBanner = computed(() => {
  const acknowledgements = previewData.value?.acknowledgements || [];

  if (!acknowledgements.length) return true;

  const allCancelled = acknowledgements.every((ack) => ack.status === 'cancelled');

  return allCancelled;
});

const daysPending = computed(() => {
  const acknowledgement = currentAcknowledgement.value;

  if (acknowledgement?.status !== 'pending') return 0;

  return previewData.value?.days_pending;
});

const showAcknowledgementOutOfDateBanner = computed(() => (
  previewData.value?.acknowledgement_alert === 'acknowledgement_out_of_date'
));

const showJobProfileChangedBanner = computed(() => (
  ['job_profile_id_changed', 'job_profile_content_changed'].includes(previewData.value?.acknowledgement_alert)
));

const jobProfileChangedBannerDescription = computed(() => (
  previewData.value?.acknowledgement_alert === 'job_profile_content_changed'
    ? "The content of this employee's job profile has changed since they last signed an acknowledgement."
    : "This employee's job profile has changed since they last signed an acknowledgement."
));

const isEmployeeActive = computed(() => Boolean(previewData.value?.is_active));
const hasEmployeeEmail = computed(() => Boolean(previewData.value?.email));

const hasJobProfile = computed(() => Boolean(previewData.value?.job_profile_ulid
  || previewData.value?.job_profile_title));

const canCreateRemindCancel = computed(() => (
  isEmployeeActive.value && hasEmployeeEmail.value && hasJobProfile.value
));

const JOB_PROFILE_REQUIRED_TOOLTIP = 'Please assign a job profile to this employee in your HRIS in order to '
  + 'create an acknowledgement.';
const COMPARE_JOB_PROFILE_REQUIRED_TOOLTIP = 'Please assign a job profile to this employee in your HRIS in order to '
  + 'compare job profiles.';
const EMAIL_REQUIRED_FOR_CREATE_TOOLTIP = `Acknowledgements can only be sent to employees with an e-mail address. 
  Please update your employee's e-mail address in your HRIS.`;
const SEND_REMINDER_EMAIL_TOOLTIP = 'Acknowledgements can only be sent to employees with an e-mail address. '
  + "Please update your employee's e-mail address in your HRIS.";

const bannerCreateDisabledTooltip = computed(() => {
  if (!hasJobProfile.value) return JOB_PROFILE_REQUIRED_TOOLTIP;
  if (!hasEmployeeEmail.value) return EMAIL_REQUIRED_FOR_CREATE_TOOLTIP;

  return '';
});

const PENDING_BLOCKS_CREATE_TOOLTIP = 'Current pending acknowledgement must be resolved prior to creating a new one.';

const isPendingCurrentAcknowledgement = computed(() => (
  currentAcknowledgement.value?.status === 'pending'
));

const createBannerActionEnabled = computed(() => (
  canCreateRemindCancel.value && !isPendingCurrentAcknowledgement.value
));

const compareJobProfilesEnabled = computed(() => (
  isEmployeeActive.value && hasJobProfile.value
));

const compareDisabledTooltip = computed(() => {
  if (!hasJobProfile.value) return COMPARE_JOB_PROFILE_REQUIRED_TOOLTIP;

  return '';
});

const createBannerDisabledTooltip = computed(() => {
  if (isPendingCurrentAcknowledgement.value) return PENDING_BLOCKS_CREATE_TOOLTIP;

  return bannerCreateDisabledTooltip.value;
});

const sendReminderDisabledTooltip = computed(() => {
  if (!hasJobProfile.value) return JOB_PROFILE_REQUIRED_TOOLTIP;
  if (!hasEmployeeEmail.value) return SEND_REMINDER_EMAIL_TOOLTIP;

  return '';
});

const canSendReminder = computed(() => hasJobProfile.value && hasEmployeeEmail.value);

const showingAnyBanner = computed(() => (
  Boolean(showNoCompletedAcknowledgementBanner.value
    || showAcknowledgementOutOfDateBanner.value
    || showJobProfileChangedBanner.value
    || isPendingCurrentAcknowledgement.value)
));

const showInlineCreateButton = computed(() => (
  isEmployeeActive.value
  && currentAcknowledgement.value
  && !showingAnyBanner.value
));

const openCreateAcknowledgementModal = () => {
  if (!previewData.value) return;

  const employeeName = [previewData.value.first_name, previewData.value.last_name].filter(Boolean).join(' ');

  openCreateModal({
    employeeName,
    employeeEmail: previewData.value.email,
    jobProfile: previewData.value.job_profile_title,
    employeeUlid: previewData.value.ulid,
    job_profile_ulid: currentAcknowledgement.value?.job_profile_ulid,
  }, 'existing');
};

const openReminderModal = () => {
  if (!previewData.value) return;

  openSendReminderModal({
    ulid: previewData.value.ulid,
    first_name: previewData.value.first_name,
    last_name: previewData.value.last_name,
    email: previewData.value.email,
    job_profile_title: previewData.value.job_profile_title,
  });
};

const openCancelAcknowledgementModal = () => {
  if (!previewData.value) return;

  openCancelModal({
    ulid: previewData.value.ulid,
    first_name: previewData.value.first_name,
    last_name: previewData.value.last_name,
    email: previewData.value.email,
    job_profile_title: previewData.value.job_profile_title,
  });
};

const openCompareJobProfiles = () => {
  if (!previewData.value || !currentAcknowledgement.value) return;

  openCompareModal({
    oldProfileUlid: currentAcknowledgement.value.job_profile_ulid,
    oldVersionUlid: currentAcknowledgement.value.job_profile_version_ulid,
    newProfileUlid: previewData.value.job_profile_ulid,
    newVersionUlid: previewData.value.job_profile_version_ulid,
    alertType: previewData.value.acknowledgement_alert,
    signedAt: currentAcknowledgement.value.signed_at,
  });
};

const handleHistoryCancel = (ack) => {
  if (!previewData.value) return;

  const jobProfileTitle = [ack.title, ack.hris_job_code].filter(Boolean).join(' ');

  openCancelModal({
    ulid: previewData.value.ulid,
    first_name: previewData.value.first_name,
    last_name: previewData.value.last_name,
    email: previewData.value.email,
    job_profile_title: jobProfileTitle || previewData.value.job_profile_title,
  });
};

watch(
  [employeeUlid, type],
  () => {
    if (employeeUlid.value && type.value === 'employee') {
      loadPreview(employeeUlid.value);
      loadAcknowledgementsConfig();
    } else {
      clearPreview();
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="less">
.employee-acknowledgement-preview {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: -24px;
  margin-right: -24px;
  width: calc(100% + 48px);
}

.preview-name-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;

  &.preview-section-banner {
    padding: 0 10px;
  }
}

.section-title {
  margin-bottom: 4px;
}

.preview-info-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  column-gap: 16px;
  row-gap: 4px;
  line-height: 1.5;
}

.preview-info-label {
  white-space: nowrap;
}

.preview-email {
  line-height: 100% !important;
}

.inline-create-disabled-wrapper {
  display: inline-block;
}
</style>
